import {
  ALL_AVAILABLE_SERVICES,
  COMMUNITY_SERVICE_ALIAS_TO_KEY,
  COMMUNITY_SERVICE_DEFINITIONS,
  COMMUNITY_SERVICE_KEYS,
  REVERSE_SPECIALIZATION_MAPPINGS,
  SERVICE_LABEL_TO_CANONICAL,
} from "../generated/careTaxonomy";
import {
  CARE_SERVICE_GROUP_ORDER,
  CARE_SERVICE_GROUP_TITLES,
  CONDITION_GROUPINGS,
  GENERAL_SERVICE_GROUPINGS,
} from "../constants/careServiceGroups";
import type {
  CareServiceCatalogGroup,
  CommunityServiceCatalogEntry,
} from "./careTaxonomySync";
import {
  deriveProviderCardCapabilityChips,
} from "../components/providers/providerCardCapabilityChips";
import { getTypicalCommunitySuggestEntries } from "./providerTypeServiceAffinity";
import type {
  ServiceCapability,
  ServiceCapabilityCoverageMode,
  ServiceCapabilitySummary,
} from "../types/serviceCapability";

/**
 * Source-backed community service capability display helpers (plan:
 * `docs/plans/provider_community_service_plan/provider-capabilities-business-services-frontend.md`,
 * Phase 1). This is the frontend's single util for **source-backed** service capabilities.
 *
 * Ownership: keep separate from `providerCapabilityFields.ts` (physical/accessibility normalization)
 * and `providerCardCapabilityChips.ts` (legacy free-text chip derivation).
 *
 * Group ordering follows the taxonomy definition order so stable group headings render predictably.
 * A provider row may expose either `service_capabilities_summary` (search/list) or
 * `service_capabilities` (detail); both are fed through the same shaping below.
 */

/** Value object for a single community-service chip. */
export interface CommunityServiceChip {
  /** Canonical community service_key (e.g. `respite_care`). */
  key: string;
  /** Display label (full human label; rail truncation is a display-layer concern). */
  label: string;
  /** Full human label for a11y title / profile. */
  fullLabel: string;
  /** Taxonomy group (behavioral_health, case_management, community_services_supports, ...). */
  group: string;
  /** Source-backed evidence label (DHS licensed / certified / MHCP enrolled / provider reported). */
  evidenceLabel?: string;
  lastVerifiedAt?: string;
  serviceArea?: string;
  /** Origin of the chip: source-backed community payload vs legacy free-text fallback. */
  chipType: "community" | "legacy";
  /** Reference back to the raw summary/capability for tooltip metadata. */
  raw?: ServiceCapability | ServiceCapabilitySummary;
}

/**
 * Attribute-views over the detail `service_capabilities[]`, one per rendering panel
 * (plan Phase 4 / §6.7#4). These are **overlapping** views of the same objects, not a strict
 * partition — a single capability with evidence + coverage + referrals appears in multiple panels
 * so each panel renders only the attributes it owns.
 */
export interface ServiceCapabilitySegments {
  /** Capabilities that form the primary "Services & supports" list. */
  services: ServiceCapability[];
  /** Capabilities carrying licensure/certification/enrollment evidence metadata. */
  evidence: ServiceCapability[];
  /** Capabilities carrying service-area / coverage info. */
  serviceArea: ServiceCapability[];
  /** Capabilities with an explicit referral availability status. */
  availability: ServiceCapability[];
}

/** Provider-like source with optional structured capabilities + legacy free text (for fallback). */
export interface ServiceCapabilityProviderSource {
  service_capabilities_summary?: ServiceCapabilitySummary[];
  service_capabilities?: ServiceCapability[];
  services_offered?: string;
  specializations?: string;
  /** Used to suppress affinity-rare clinical chips on home_care cards. */
  business_type?: string | null;
}

type CommunityServiceDefinition = (typeof COMMUNITY_SERVICE_DEFINITIONS)[number];

const _DEFINITION_BY_KEY = new Map<string, CommunityServiceDefinition>(
  COMMUNITY_SERVICE_DEFINITIONS.map(
    (d) => [d.key, d] as [string, CommunityServiceDefinition]
  )
);

/** Deterministic group/definition order from the taxonomy (independent of input encounter order). */
const _GROUP_ORDER = new Map<string, number>();
const _KEY_ORDER = new Map<string, number>();
COMMUNITY_SERVICE_DEFINITIONS.forEach((d, i) => {
  if (!_GROUP_ORDER.has(d.group)) {
    _GROUP_ORDER.set(d.group, _GROUP_ORDER.size);
  }
  if (!_KEY_ORDER.has(d.key)) {
    _KEY_ORDER.set(d.key, i);
  }
});

/** Fallback shape for unknown / not-yet-registered service keys (never show a raw slug to users). */
const UNKNOWN_SERVICE_FALLBACK = {
  label: "",
  group: "other",
  coverageMode: "explicit_service_area" as ServiceCapabilityCoverageMode,
};

/**
 * Resolve a canonical `service_key` to taxonomy display metadata.
 * Unknown keys degrade to a humanized label (title-cased), never the raw slug.
 */
export function mapServiceKeyToTaxonomy(
  key: string
): { label: string; group: string; coverageMode: ServiceCapabilityCoverageMode } {
  const entry = _DEFINITION_BY_KEY.get(key);
  if (entry) {
    return {
      label: entry.label,
      group: entry.group,
      coverageMode: entry.coverageMode,
    };
  }
  // Accept label/alias input gracefully (alias → key → definition).
  const canonical = COMMUNITY_SERVICE_ALIAS_TO_KEY[key.trim().toLowerCase()];
  if (canonical) {
    return mapServiceKeyToTaxonomy(canonical);
  }
  // Humanized last resort — never a raw slug (plan unresolved-label policy: unknown keys
  // render with non-slug labels and are flagged for taxonomy/backfill review).
  return { ...UNKNOWN_SERVICE_FALLBACK, label: humanizeServiceKey(key) };
}

function shield(
  value: string | undefined,
  preferred: string
): string {
  const trimmed = (value || "").trim();
  return trimmed.length > 0 ? trimmed : preferred;
}

/**
 * Last-resort humanized label for an unknown `service_key` — title-cased, never the raw
 * slug (plan error-handling: unknown keys render with non-slug fallback labels and are
 * flagged for taxonomy/backfill review).
 */
export function humanizeServiceKey(key: string): string {
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function toCapabilityList(
  source: ServiceCapabilityProviderSource
): ServiceCapabilitySummary[] | ServiceCapability[] {
  return source.service_capabilities_summary ?? source.service_capabilities ?? [];
}

/**
 * Derive source-backed community-service chips from a search or detail provider row.
 * Deduplicated by canonical `service_key`, ordered by taxonomy definition (stable group order).
 */
export function deriveCommunityServiceChips(
  source: ServiceCapabilityProviderSource
): CommunityServiceChip[] {
  const capabilities = toCapabilityList(source);
  if (capabilities.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const chips: CommunityServiceChip[] = [];

  for (const cap of capabilities) {
    const key = cap.service_key;
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);

    const taxonomy = mapServiceKeyToTaxonomy(key);
    // Last resort is the (humanized) taxonomy fallback — never the raw slug.
    const fullLabel = shield(cap.display_label, taxonomy.label);

    let evidenceLabel: string | undefined;
    let lastVerifiedAt: string | undefined;
    let serviceArea: string | undefined;
    if ("evidence_label" in cap) {
      evidenceLabel = cap.evidence_label;
    }
    if ("last_verified_at" in cap) {
      lastVerifiedAt = cap.last_verified_at;
    }
    if ("service_area_summary" in cap) {
      serviceArea = cap.service_area_summary;
    } else if ("service_area" in cap) {
      serviceArea = cap.service_area;
    }

    chips.push({
      key,
      label: fullLabel,
      fullLabel,
      group: taxonomy.group,
      evidenceLabel,
      lastVerifiedAt,
      serviceArea,
      chipType: "community",
      raw: cap,
    });
  }

  // Stable group ordering: taxonomy definition order (group, then definition), independent of input.
  return [...chips].sort((a, b) => {
    const ga = _GROUP_ORDER.get(a.group) ?? _GROUP_ORDER.size;
    const gb = _GROUP_ORDER.get(b.group) ?? _GROUP_ORDER.size;
    if (ga !== gb) return ga - gb;
    const ka = _KEY_ORDER.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const kb = _KEY_ORDER.get(b.key) ?? Number.MAX_SAFE_INTEGER;
    return ka - kb;
  });
}

/** Shaped community-service labels for compare / worksheet surfaces (plan Phase 6). */
export type CommunityServicesCompareShape = {
  /** Plain service labels (no evidence suffix), capped for "few + more". */
  communitySummary: string[];
  /** Plain service labels (full list). */
  communityDetail: string[];
  hasCommunity: boolean;
  /**
   * Set when every listed service shares the same non-empty evidence label.
   * Callers should show this once (footnote / ` · label`) instead of repeating per item.
   */
  sharedEvidenceLabel: string | null;
  /**
   * Evidence labels aligned with `communityDetail` (and the prefix of `communitySummary`).
   * Used when `sharedEvidenceLabel` is null so mixed evidence can still be shown per item.
   */
  detailEvidenceLabels: (string | undefined)[];
};

/**
 * When every chip has the same non-empty evidence label, return that label; otherwise null
 * (mixed or missing → callers suffix selectively via `detailEvidenceLabels`).
 */
function resolveSharedEvidenceLabel(
  evidenceLabels: (string | undefined)[]
): string | null {
  if (evidenceLabels.length === 0) return null;
  const trimmed = evidenceLabels.map((label) => (label || "").trim());
  if (trimmed.some((label) => !label)) return null;
  const first = trimmed[0];
  return trimmed.every((label) => label === first) ? first : null;
}

/**
 * Format a provider row's source-backed community services for comparison/worksheet surfaces
 * (plan Phase 6). Produces plain service labels plus evidence metadata so callers can avoid
 * repeating a homogeneous suffix (e.g. every item "(DHS licensed)") while still distinguishing
 * DHS-certified vs self-reported when evidence is mixed.
 *
 * Prefer `joinCommunityServicesCompareCell` for the final cell string.
 *
 * Deliberately NO legacy free-text fallback: these surfaces are labeled "Community services &
 * supports" and must only ever show source-backed entries. Generic `services_offered` content is
 * already covered by the separate Services row; proxying it here would mislabel evidence-free
 * free text as source-backed community services.
 */
export function formatCommunityServicesForCompare(
  source: ServiceCapabilityProviderSource
): CommunityServicesCompareShape {
  const chips = deriveCommunityServiceChips(source);
  if (chips.length > 0) {
    const detail = chips.map((chip) => chip.fullLabel);
    const detailEvidenceLabels = chips.map((chip) => chip.evidenceLabel);
    return {
      // "few chips + more": cap matches the CARD_CAPABILITY_CHIP_LIMIT convention used by the rail.
      communitySummary: detail.slice(0, COMMUNITY_COMPARE_VISIBLE_CHIP_LIMIT),
      communityDetail: detail,
      hasCommunity: true,
      sharedEvidenceLabel: resolveSharedEvidenceLabel(detailEvidenceLabels),
      detailEvidenceLabels,
    };
  }

  // No community capability payload → nothing source-backed to show (callers render
  // "None listed"; the plain Services row covers the legacy free-text list).
  return {
    communitySummary: [],
    communityDetail: [],
    hasCommunity: false,
    sharedEvidenceLabel: null,
    detailEvidenceLabels: [],
  };
}

/**
 * Build the compare/worksheet cell string from a shaped community-services payload.
 *
 * - Homogeneous evidence → plain names (+N more) once, then ` · {evidence}` footnote.
 * - Mixed / partial evidence → per-item `(evidence)` only when present; no shared footnote.
 */
export function joinCommunityServicesCompareCell(
  shaped: CommunityServicesCompareShape
): string {
  if (!shaped.hasCommunity || shaped.communitySummary.length === 0) {
    return "None listed";
  }

  const visibleItems = shaped.communitySummary.map((label, index) => {
    if (shaped.sharedEvidenceLabel) {
      return label;
    }
    const evidence = (shaped.detailEvidenceLabels[index] || "").trim();
    return evidence ? `${label} (${evidence})` : label;
  });
  let text = visibleItems.join(", ");

  if (shaped.communityDetail.length > shaped.communitySummary.length) {
    const hiddenCount =
      shaped.communityDetail.length - shaped.communitySummary.length;
    text = `${text} (+${hiddenCount} more)`;
  }

  if (shaped.sharedEvidenceLabel) {
    text = `${text} · ${shaped.sharedEvidenceLabel}`;
  }
  return text;
}

/** How many community-service entries the compare/worksheet surfaces show before "+N more". */
export const COMMUNITY_COMPARE_VISIBLE_CHIP_LIMIT = 3;

/**
 * Segment full `service_capabilities` into the four attribute-views used by the provider-detail
 * panels (plan §6.7#4). Views overlap by design (see `ServiceCapabilitySegments`).
 */
export function segmentServiceCapabilities(
  capabilities: ServiceCapability[] | undefined
): ServiceCapabilitySegments {
  const list = Array.isArray(capabilities) ? capabilities : [];
  return {
    services: list.filter(
      (c) => !!c.service_key && !!c.display_label
    ),
    evidence: list.filter(
      (c) => !!c.evidence_type || !!c.evidence_source_name || !!c.last_verified_at
    ),
    serviceArea: list.filter(
      (c) => !!c.service_area || !!c.coverage_mode
    ),
    // `null` = unknown: include it so ServicesTab renders "unknown" explicitly
    // (plan §6.7#4) instead of hiding the row — `formatReferralStatus` handles null.
    availability: list.filter((c) => "accepting_referrals" in c),
  };
}

/** Human-label expansion for `accepting_referrals` tri-state (never hides "unknown"). */
export function formatReferralStatus(
  value: boolean | null | undefined
): string {
  if (value === true) return "Accepting referrals";
  if (value === false) return "Not accepting referrals";
  return "Referral availability unknown";
}

/** Human label for evidence_type enums (never show raw `license` / snake_case). */
export function formatEvidenceType(
  value: string | null | undefined
): string | null {
  const raw = (value || "").trim();
  if (!raw) return null;
  const known: Record<string, string> = {
    license: "License",
    certification: "Certification",
    enrollment: "Enrollment",
    network: "Network",
    contracted_provider: "Contracted provider",
    resource_directory: "Resource directory",
    registry: "Registry",
    derived: "Derived from directory",
    provider_reported: "Provider reported",
    provider_confirmed: "Provider confirmed",
  };
  const key = raw.toLowerCase().replace(/\s+/g, "_");
  return known[key] ?? humanizeServiceKey(raw);
}

/**
 * Human label for coverage_mode. Prefer not showing this alone as a "service area";
 * use `formatServiceAreaForDisplay` for visitor-facing area copy.
 */
export function formatCoverageMode(
  value: string | null | undefined
): string | null {
  const raw = (value || "").trim();
  if (!raw) return null;
  if (raw === "explicit_service_area") {
    return "Listed service area (not based on office distance)";
  }
  if (raw === "facility_distance") {
    return "Based on facility location";
  }
  return humanizeServiceKey(raw);
}

/**
 * Format ISO / offset timestamps for capability evidence cards.
 * Returns null when unparseable so callers can omit the row.
 */
export function formatCapabilityTimestamp(
  value: string | null | undefined
): string | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(ms));
  } catch {
    return null;
  }
}

/**
 * Visitor-facing service-area line for one capability.
 * Never surfaces raw `coverage_mode` enums. When mode is explicit but no geography
 * is published, say so honestly (do not imply office-distance coverage).
 */
export function formatServiceAreaForDisplay(
  cap: Pick<
    ServiceCapability,
    "service_area" | "coverage_mode" | "display_label" | "service_key"
  >
): string {
  const area = (cap.service_area || "").trim();
  if (area) return area;

  const mode = (cap.coverage_mode || "").trim();
  if (mode === "explicit_service_area" || !mode) {
    return "Service area not published";
  }
  if (mode === "facility_distance") {
    return "Coverage based on facility location";
  }
  return "Service area not published";
}

export interface ReferralAvailabilitySummary {
  /** Capabilities with a known yes/no referral status (show per row). */
  known: ServiceCapability[];
  /** Count of capabilities where referral status is unknown/null. */
  unknownCount: number;
  /** Compact copy when every listed capability is unknown. */
  allUnknownLabel: string;
}

/**
 * Collapse noisy "unknown" referral rows into a summary while keeping known statuses.
 */
export function summarizeReferralAvailability(
  capabilities: ServiceCapability[] | undefined
): ReferralAvailabilitySummary {
  const list = Array.isArray(capabilities) ? capabilities : [];
  const known: ServiceCapability[] = [];
  let unknownCount = 0;
  for (const cap of list) {
    if (!("accepting_referrals" in cap)) continue;
    if (cap.accepting_referrals === true || cap.accepting_referrals === false) {
      known.push(cap);
    } else {
      unknownCount += 1;
    }
  }
  return {
    known,
    unknownCount,
    allUnknownLabel: "Referral status not reported",
  };
}

const SERVICE_AREA_NOT_PUBLISHED = "Service area not published";

export interface ServiceAreaDisplayLine {
  /** Omitted when the line is a section-wide summary. */
  label?: string;
  text: string;
}

/**
 * Collapse repetitive service-area rows on the profile (e.g. four identical
 * "not published" lines → one summary).
 */
export function summarizeServiceAreaDisplay(
  capabilities: ServiceCapability[] | undefined,
  options?: { maxRows?: number }
): ServiceAreaDisplayLine[] {
  const maxRows = options?.maxRows ?? 12;
  const caps = (Array.isArray(capabilities) ? capabilities : []).slice(
    0,
    maxRows
  );
  if (caps.length === 0) return [];

  const formatted = caps.map((cap) => ({
    label: (cap.display_label || "").trim(),
    text: formatServiceAreaForDisplay(cap),
  }));

  const uniqueTexts = new Set(formatted.map((f) => f.text));
  if (uniqueTexts.size === 1) {
    return [{ text: formatted[0].text }];
  }

  const withGeo = formatted.filter((f) => f.text !== SERVICE_AREA_NOT_PUBLISHED);
  const unpublished = formatted.filter(
    (f) => f.text === SERVICE_AREA_NOT_PUBLISHED
  );

  if (withGeo.length === 0) {
    return [{ text: SERVICE_AREA_NOT_PUBLISHED }];
  }

  const lines: ServiceAreaDisplayLine[] = withGeo.map((f) => ({
    label: f.label,
    text: f.text,
  }));

  if (unpublished.length > 0) {
    lines.push({
      text:
        unpublished.length === 1 && unpublished[0].label
          ? `${SERVICE_AREA_NOT_PUBLISHED} (${unpublished[0].label})`
          : `${SERVICE_AREA_NOT_PUBLISHED} for ${unpublished.length} services`,
    });
  }

  return lines;
}

export interface EvidenceSourceGroup {
  /** Source display name, or a stable fallback when missing. */
  sourceName: string;
  sourceUrl?: string | null;
  evidenceTypeLabel: string | null;
  capabilities: ServiceCapability[];
}

/**
 * Group evidence-bearing capabilities by source so detail can show one card per
 * directory/source instead of repeating near-identical cards per service.
 */
export function groupEvidenceBySource(
  capabilities: ServiceCapability[] | undefined
): EvidenceSourceGroup[] {
  const list = (Array.isArray(capabilities) ? capabilities : []).filter(
    (c) => !!c.evidence_type || !!c.evidence_source_name || !!c.last_verified_at
  );
  const order: string[] = [];
  const byKey = new Map<string, EvidenceSourceGroup>();

  for (const cap of list) {
    const sourceName =
      (cap.evidence_source_name || "").trim() || "Directory source";
    const key = `${sourceName}::${(cap.source_url || "").trim()}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        sourceName,
        sourceUrl: cap.source_url,
        evidenceTypeLabel: formatEvidenceType(cap.evidence_type),
        capabilities: [],
      };
      byKey.set(key, group);
      order.push(key);
    }
    group.capabilities.push(cap);
    if (!group.evidenceTypeLabel) {
      group.evidenceTypeLabel = formatEvidenceType(cap.evidence_type);
    }
  }

  return order.map((k) => byKey.get(k)!);
}

/**
 * One group of community-service filter options (plan Phase 5). `value` is the canonical
 * `service_key` (sent as `required_service_keys`); `label` is the display label.
 */
export interface CommunityServiceFilterGroup {
  /** Stable group key from `COMMUNITY_SERVICE_DEFINITIONS[].group`. */
  group: string;
  /** Human group heading for the filter UI. */
  title: string;
  /** Options for the filter panel (`value` = canonical service_key). */
  options: { value: string; label: string }[];
}

const COMMUNITY_GROUP_TITLES: Record<string, string> = CARE_SERVICE_GROUP_TITLES;

/** Human-readable taxonomy group title (tooltips, headings); humanizes unknown groups. */
export function communityGroupTitle(group: string): string {
  return COMMUNITY_GROUP_TITLES[group] ?? humanizeServiceKey(group);
}

/**
 * Community keys whose token also appears in the general care-service taxonomy and would create a
 * duplicated/overlapping option if surfaced in the community filter group. The plan's overlap rule
 * says to skip these so users never see the same service in two groups (see Field/contract shapes).
 */
const COMMUNITY_OVERLAP_SKIP_KEYS = new Set<string>(["respite_care"]);

/**
 * Build the "Community services & supports" filter groups from the taxonomy, grouped by `group`,
 * ordered by taxonomy definition order. Skips entries whose display label exactly matches a general
 * care-service option OR that are marked as overlapping (e.g. `respite_care`).
 */
export function buildCommunityServiceFilterGroups(options?: {
  communityServices?: readonly CommunityServiceCatalogEntry[];
  discoveryGroups?: readonly CareServiceCatalogGroup[];
}): CommunityServiceFilterGroup[] {
  const generalLabels = new Set(
    ALL_AVAILABLE_SERVICES.map((l) => l.trim().toLowerCase())
  );
  const groupTitles = new Map(
    (options?.discoveryGroups ?? []).map((group) => [group.key, group.title])
  );
  const definitions = options?.communityServices ?? COMMUNITY_SERVICE_DEFINITIONS;

  const groups: CommunityServiceFilterGroup[] = [];
  const byGroup = new Map<string, CommunityServiceFilterGroup>();

  for (const def of definitions) {
    // Retired-from-search entries stay registered in the taxonomy (case/registration
    // surfaces still resolve them) but never appear as search filter options (v7).
    if (def.inSearchFilters === false) {
      continue;
    }
    if (COMMUNITY_OVERLAP_SKIP_KEYS.has(def.key)) {
      continue;
    }
    // Skip if this community label is already offered as a generic care-service option.
    if (generalLabels.has(def.label.trim().toLowerCase())) {
      continue;
    }
    let group = byGroup.get(def.group);
    if (!group) {
      group = {
        group: def.group,
        title:
          groupTitles.get(def.group) ?? COMMUNITY_GROUP_TITLES[def.group] ?? def.group,
        options: [],
      };
      byGroup.set(def.group, group);
      groups.push(group);
    }
    group.options.push({ value: def.key, label: def.label });
  }

  return groups;
}

/** Flatten all community group options into a lookup for selection helpers. */
export function buildCommunityServiceOptionLookup(): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const group of buildCommunityServiceFilterGroups()) {
    for (const opt of group.options) {
      lookup.set(opt.value, opt.label);
    }
  }
  return lookup;
}

/**
 * Resolve a legacy free-text label (services_offered / specializations) to a canonical community
 * `service_key` when it denotes the same service. Uses, in order: the raw key, the community alias
 * map, the general service label canonicalization, and the alias of the canonical form / reverse
 * specialization map (e.g. "Respite Care / Short-Term Stay" → canonical "Respite Care" → the
 * community `respite_care` key).
 */
export function resolveLegacyLabelToCommunityKey(label: string): string | null {
  const trimmed = label.trim();
  if (!trimmed) return null;
  if (COMMUNITY_SERVICE_KEYS.has(trimmed)) return trimmed;
  const viaAlias = COMMUNITY_SERVICE_ALIAS_TO_KEY[trimmed.toLowerCase()];
  if (viaAlias) return viaAlias;
  const canonical = SERVICE_LABEL_TO_CANONICAL[trimmed];
  if (canonical) {
    const viaCanonicalAlias =
      COMMUNITY_SERVICE_ALIAS_TO_KEY[canonical.trim().toLowerCase()];
    if (viaCanonicalAlias) return viaCanonicalAlias;
    const viaSlug = REVERSE_SPECIALIZATION_MAPPINGS[canonical];
    if (viaSlug && COMMUNITY_SERVICE_KEYS.has(viaSlug)) return viaSlug;
    if (viaSlug && COMMUNITY_SERVICE_ALIAS_TO_KEY[viaSlug.toLowerCase()]) {
      return COMMUNITY_SERVICE_ALIAS_TO_KEY[viaSlug.toLowerCase()];
    }
  }
  return null;
}

/**
 * True when a legacy free-text label denotes a service already covered (with source-backed
 * evidence) by one of the community chips — used to drop duplicate general-care chips and
 * labels (plan Phase 1 #2 overlap/precedence rule; map popup "Services" line).
 */
export function isLegacyLabelCoveredByCommunityChips(
  label: string,
  communityChips: CommunityServiceChip[]
): boolean {
  const norm = label.trim().toLowerCase();
  if (!norm) return false;
  for (const chip of communityChips) {
    if (chip.fullLabel.trim().toLowerCase() === norm) return true;
  }
  const resolvedKey = resolveLegacyLabelToCommunityKey(label);
  // Only treat as covered when the provider's own chips include that key — a mere
  // taxonomy overlap must not hide legacy services the provider still reports.
  return resolvedKey !== null && communityChips.some((chip) => chip.key === resolvedKey);
}

/**
 * Resolve the capability chip set for a provider row. Legacy free-text chips are merged with
 * source-backed community chips when the structured payload is present: community chips lead and
 * overlapping legacy duplicates are dropped (community wins), but non-overlapping legacy services
 * stay visible (plan Phase 1 #2 / Phase 2). Without the payload, behavior matches the pre-existing
 * legacy rail exactly.
 */
export type ProviderCapabilityChipSource =
  | "community"
  | "legacy"
  | "type_default";

export function resolveProviderCapabilityChips(
  source: ServiceCapabilityProviderSource,
  options?: {
    preferShortLabels?: boolean;
    limit?: number;
    /**
     * Active search filter Care Type. Peer-gated inside chip derivation
     * (Plan 32 B9) — only overrides rare affinity for search peers.
     */
    rareAffinityBusinessType?: string | null;
  }
): {
  chips: CommunityServiceChip[];
  totalCount: number;
  hiddenCount: number;
  source: ProviderCapabilityChipSource;
} {
  const preferShortLabels = options?.preferShortLabels;
  const limit = options?.limit;
  const rareAffinityBusinessType = options?.rareAffinityBusinessType;
  const community = deriveCommunityServiceChips(source);

  if (community.length === 0) {
    // Type defaults only when there is no source-backed community payload and no
    // usable legacy services/specializations.
    const legacy = deriveProviderCardCapabilityChips(
      source.services_offered || "",
      source.specializations || "",
      {
        preferShortLabels,
        businessType: source.business_type,
        rareAffinityBusinessType,
        useTypeDefaultsWhenEmpty: true,
        ...(limit != null ? { limit } : {}),
      }
    );
    // Community-led invent: expose taxonomy keys so chips match suggested supports.
    if (legacy.usedTypeDefaults) {
      const communityTypical = getTypicalCommunitySuggestEntries(
        source.business_type ?? undefined
      );
      if (communityTypical.length > 0) {
        const byLabel = new Map(
          communityTypical.map((entry) => [
            entry.label.trim().toLowerCase(),
            entry,
          ])
        );
        const inventChips: CommunityServiceChip[] = legacy.chips.map((chip) => {
          const entry = byLabel.get(chip.fullLabel.trim().toLowerCase());
          if (entry) {
            const taxonomy = mapServiceKeyToTaxonomy(entry.key);
            return {
              key: entry.key,
              label: chip.label,
              fullLabel: chip.fullLabel,
              group: taxonomy.group,
              chipType: "community" as const,
            };
          }
          return {
            key: `service-${chip.fullLabel}`,
            label: chip.label,
            fullLabel: chip.fullLabel,
            group: "other",
            chipType: "legacy" as const,
          };
        });
        return {
          chips: inventChips,
          totalCount: legacy.totalCount,
          hiddenCount: legacy.hiddenCount,
          source: "type_default",
        };
      }
    }
    return {
      chips: legacy.chips.map((chip) => ({
        key: chip.source === "service" ? `service-${chip.fullLabel}` : `specialization-${chip.fullLabel}`,
        label: chip.label,
        fullLabel: chip.fullLabel,
        group: "other",
        chipType: "legacy" as const,
      })),
      totalCount: legacy.totalCount,
      hiddenCount: legacy.hiddenCount,
      // Plan 29 P2.3: distinguish empty→typical invent from stored legacy chips.
      source: legacy.usedTypeDefaults ? "type_default" : "legacy",
    };
  }

  // Uncap the legacy derivation so overlap filtering sees every free-text chip; the
  // list-card `limit` then slices the merged rail. Do not invent type defaults here —
  // community evidence already answers "what services?" for this row.
  const legacyAll = deriveProviderCardCapabilityChips(
    source.services_offered || "",
    source.specializations || "",
    {
      preferShortLabels,
      businessType: source.business_type,
      rareAffinityBusinessType,
      limit: Number.MAX_SAFE_INTEGER,
    }
  );
  const merged: CommunityServiceChip[] = [
    ...community,
    ...legacyAll.chips
      .filter((chip) => !isLegacyLabelCoveredByCommunityChips(chip.fullLabel, community))
      .map((chip) => ({
        key: chip.source === "service" ? `service-${chip.fullLabel}` : `specialization-${chip.fullLabel}`,
        label: chip.label,
        fullLabel: chip.fullLabel,
        group: "other",
        chipType: "legacy" as const,
      })),
  ];
  const visible =
    limit != null ? merged.slice(0, limit) : merged;
  return {
    chips: visible,
    totalCount: merged.length,
    hiddenCount: Math.max(0, merged.length - visible.length),
    source: "community",
  };
}

/**
 * Build "optgroup-style" option groups for a Care Services & Specialized Support panel, ordered by the
 * shared discovery-group order (plan v4 §12.4). Options may be plain labels (the shape of
 * `ALL_AVAILABLE_SERVICES`) or `{value,label}` pairs; values are canonicalized via
 * `SERVICE_LABEL_TO_CANONICAL` before the `GENERAL_SERVICE_GROUPINGS` lookup. Any option
 * that fails to resolve lands under a trailing "Other care services" bucket so it is never
 * dropped from the UI (guard-tested — see plan §12.5).
 */
export interface CareServiceOptionGroup {
  title: string;
  options: { value: string; label: string }[];
}

type RawPanelOption = string | { value: string; label: string };

function toPanelOption(raw: RawPanelOption): { value: string; label: string } {
  return typeof raw === "string"
    ? { value: raw, label: raw }
    : { value: raw.value, label: raw.label };
}

export function buildGeneralServiceOptionGroups(
  options: readonly RawPanelOption[]
): CareServiceOptionGroup[] {
  const byGroup = new Map<string, { value: string; label: string }[]>();
  const ungrouped: { value: string; label: string }[] = [];

  for (const raw of options) {
    const option = toPanelOption(raw);
    const canonical = SERVICE_LABEL_TO_CANONICAL[option.value] ?? option.value;
    const group =
      GENERAL_SERVICE_GROUPINGS[canonical] ??
      GENERAL_SERVICE_GROUPINGS[option.value];
    if (!group) {
      ungrouped.push(option);
      continue;
    }
    let bucket = byGroup.get(group);
    if (!bucket) {
      bucket = [];
      byGroup.set(group, bucket);
    }
    bucket.push(option);
  }

  const groups: CareServiceOptionGroup[] = CARE_SERVICE_GROUP_ORDER.filter(
    (group) => byGroup.has(group)
  ).map((group) => ({
    title: CARE_SERVICE_GROUP_TITLES[group],
    options: byGroup.get(group)!,
  }));

  return ungrouped.length > 0
    ? [...groups, { title: "Other care services", options: ungrouped }]
    : groups;
}

/**
 * Group Specialized Care Conditions for display (plan v4 §11.2/§11.4). Conditions are keyed
 * by their exact stored label in `CONDITION_GROUPINGS`; unresolved conditions fall into a
 * trailing "Other conditions" bucket rather than disappearing.
 */
export function buildConditionOptionGroups(
  options: readonly RawPanelOption[]
): CareServiceOptionGroup[] {
  const byGroup = new Map<string, { value: string; label: string }[]>();
  const ungrouped: { value: string; label: string }[] = [];

  for (const raw of options) {
    const option = toPanelOption(raw);
    const group = CONDITION_GROUPINGS[option.label];
    if (!group) {
      ungrouped.push(option);
      continue;
    }
    let bucket = byGroup.get(group);
    if (!bucket) {
      bucket = [];
      byGroup.set(group, bucket);
    }
    bucket.push(option);
  }

  const groups: CareServiceOptionGroup[] = CARE_SERVICE_GROUP_ORDER.filter(
    (group) => byGroup.has(group)
  ).map((group) => ({
    title: CARE_SERVICE_GROUP_TITLES[group],
    options: byGroup.get(group)!,
  }));

  return ungrouped.length > 0
    ? [...groups, { title: "Other conditions", options: ungrouped }]
    : groups;
}
